import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { getColor, type, getButtonTheme } from '../../style';
import { ButtonV2 } from 'swui';
import { IButtonProps, IContent, IIconContainer, IStyledButton } from './types/Button';
import styled from '@emotion/styled/base';
import { FONT_FAMILIES } from 'theme/material/constants';
import { isMobile } from 'react-device-detect';
import breakpoints from 'theme/material/breakpoints';

const getDefaultButtonStyle = () => `
    border: ${getButtonTheme().defaultBorder};
    color: ${getButtonTheme().darkReverse};
    overflow: hidden;
    :hover {
      background-color: ${getButtonTheme().GreyAndWhite} !important;
    };
    :focus {
      background-color: ${getButtonTheme().GreyAndWhite} !important;
    };
    :active {
      box-shadow: none !important;
    };
    :disabled {
      border-color: rgb(224, 224, 224) !important;
      color: rgba(180,180,180,0.3) !important;
      cursor: default !important;
    }
`;

const getButtonStyle = () => ({
  normal: getDefaultButtonStyle(),
  primary: `
    overflow: hidden;
    background-color: #2490de !important;
    color: ${getButtonTheme().GreyAndWhite} !important;
    border-color: #2490de !important;
    &:hover {
      color: #fff !important;
      background-color: ${getButtonTheme().hoverFocus} !important;
      border-color: rgb(28, 115, 177) !important;
    };
    &:focus {
      color: #FFF !important;
      background-color: ${getButtonTheme().hoverFocus} !important;
      border-color: rgb(28, 115, 177) !important;
    };
    &:active {
      color: #FFF !important;
      background-color: ${getButtonTheme().active} !important;
      border-color: rgb(21, 86, 133) !important;
    };
    &:disabled {
      color: #FFF !important;
      background-color: ${getButtonTheme().disabled} !important;
      border-color: rgb(167, 210, 241) !important;
      cursor: default !important;
    };
    `,
  chromeless: {
    overflow: 'hidden',
    borderWidth: '0px !important',
    borderColor: 'transparent !important',
    padding: 0,
    '&:active': {
      boxShadow: 'none !important',
      backgroundColor: 'unset !important',
    },
  },
  link: `
    color: ${getColor().BRAND_1} !important;
    border-style: none !important;
    &:active {
      box-shadow: none !important;
    };
  `,
  success: `
    overflow: hidden;
    background-color: #32CD32 !important;
    color: #FFF !important;
    border-color: #32CD32 !important;
    &:hover {
      color: #fff !important;
      background-color: #228B22 !important;
      border-color: #228B22 !important;
    };
    &:focus {
      color: #FFF !important;
      background-color: #228B22 !important;
      border-color: #228B22 !important;
    };
    &:active {
      color: #FFF !important;
      background-color: #228B22 !important;
      border-color: #228B22 !important;
    };
    &:disabled {
      color: #cbcbcb !important;
      background-color: #228B22 !important;
      border-color: #228B22 !important;
      cursor: default !important;
    };
    `,
  error: `
    overflow: hidden;
    background: linear-gradient(94deg, #E21D4A 0%, #C0103E 100%) !important;
    color: #FFF !important;
    border-color: transparent !important;
    &:hover {
      color: #fff !important;
      background: #E21D4A !important;
      border-color: #E21D4A !important;
    };
    &:focus {
      color: #FFF !important;
      background: #E21D4A !important;
      border-color: #E21D4A !important;
    };
    &:active {
      color: #EB6180 !important;
      background: #C0103E !important;
      border-color: #C0103E !important;
    };
    &:disabled {
      color: #FFF !important;
      background: #DEDEDE !important;
      border-color: #DEDEDE !important;
      cursor: default !important;
    };
    `,
  cancel: `
    overflow: hidden;
    background-color: #ffffff !important;
    color: #E21D4A !important;
    border-color: #E21D4A !important;
    &:hover {
      color: #E21D4A !important;
      background-color: #F9D2DB !important;
      border: 1px solid #E21D4A !important;
    };
    &:focus {
      color: #ffffff !important;
      background-color: #E21D4A !important;
      border-color: #E21D4A !important;
    };
    &:active {
      color: #EB6180 !important;
      background-color: #C0103E !important;
      border-color: #C0103E !important;
    };
    &:disabled {
      color: rgb(36, 144, 222, 0.5) !important;
      background-color: #ffffff !important;
      border-color: rgb(36, 144, 222, 0.5) !important;
      cursor: default !important;
    };
  `,
  secondaryBlue: `
    overflow: hidden;
    background-color: ${getButtonTheme().secondaryBlue.background} !important;
    color: ${getButtonTheme().secondaryBlue.text} !important;
    border-color:  ${getButtonTheme().secondaryBlue.border} !important;
    &:hover {
      color:  ${getButtonTheme().secondaryBlue.hover.text} !important;
      background-color: ${getButtonTheme().secondaryBlue.hover.background} !important;
      border-color: ${getButtonTheme().secondaryBlue.hover.border} !important;
    };
    &:focus {
      color:  ${getButtonTheme().secondaryBlue.hover.text} !important;
      background-color: ${getButtonTheme().secondaryBlue.hover.background} !important;
      border-color: ${getButtonTheme().secondaryBlue.hover.border} !important;
    };
    &:active {
      color:  ${getButtonTheme().secondaryBlue.active.text} !important;
      background-color: ${getButtonTheme().secondaryBlue.active.background} !important;
      border-color: ${getButtonTheme().secondaryBlue.active.border} !important;
      box-shadow: none !important;
    };
    &:disabled {
      color:  ${getButtonTheme().secondaryBlue.disabled.text} !important;
      background-color: ${getButtonTheme().secondaryBlue.disabled.background} !important;
      border-color: ${getButtonTheme().secondaryBlue.disabled.border} !important;
    };
    `,
  deactivate: `
    overflow: hidden;
    background-color: ${getButtonTheme().secondaryRed.background} !important;
    color: ${getButtonTheme().secondaryRed.text} !important;
    border-color:  ${getButtonTheme().secondaryRed.border} !important;
    &:hover {
      color:  ${getButtonTheme().secondaryRed.hover.text} !important;
      background-color: ${getButtonTheme().secondaryRed.hover.background} !important;
      border-color: ${getButtonTheme().secondaryRed.hover.border} !important;
    };
    &:focus {
      color:  ${getButtonTheme().secondaryRed.hover.text} !important;
      background-color: ${getButtonTheme().secondaryRed.hover.background} !important;
      border-color: ${getButtonTheme().secondaryRed.hover.border} !important;
    };
    &:active {
      color:  ${getButtonTheme().secondaryRed.active.text} !important;
      background-color: ${getButtonTheme().secondaryRed.active.background} !important;
      border-color: ${getButtonTheme().secondaryRed.active.border} !important;
    };
    &:disabled {
      color:  ${getButtonTheme().secondaryRed.disabled.text} !important;
      background-color: ${getButtonTheme().secondaryRed.disabled.background} !important;
      border-color: ${getButtonTheme().secondaryRed.disabled.border} !important;
    };
    `,
  primaryRed: `
    overflow: hidden;
    background-color: ${getButtonTheme().primaryRed.background} !important;
    color: ${getButtonTheme().primaryRed.text} !important;
    border-color:  ${getButtonTheme().primaryRed.border} !important;
    &:hover {
      color:  ${getButtonTheme().primaryRed.hover.text} !important;
      background-color: ${getButtonTheme().primaryRed.hover.background} !important;
      border-color: ${getButtonTheme().primaryRed.hover.border} !important;
    };
    &:focus {
      color:  ${getButtonTheme().primaryRed.hover.text} !important;
      background-color: ${getButtonTheme().primaryRed.hover.background} !important;
      border-color: ${getButtonTheme().primaryRed.hover.border} !important;
    };
    &:active {
      color:  ${getButtonTheme().primaryRed.active.text} !important;
      background-color: ${getButtonTheme().primaryRed.active.background} !important;
      border-color: ${getButtonTheme().primaryRed.active.border} !important;
    };
    &:disabled {
      color:  ${getButtonTheme().primaryRed.disabled.text} !important;
      background-color: ${getButtonTheme().primaryRed.disabled.background} !important;
      border-color: ${getButtonTheme().primaryRed.disabled.border} !important;
    };
    `,
  secondaryRed: `
    overflow: hidden;
    background-color: ${getButtonTheme().secondaryRed.background} !important;
    color: ${getButtonTheme().secondaryRed.text} !important;
    border-color:  ${getButtonTheme().secondaryRed.border} !important;
    &:hover {
      color:  ${getButtonTheme().secondaryRed.hover.text} !important;
      background-color: ${getButtonTheme().secondaryRed.hover.background} !important;
      border-color: ${getButtonTheme().secondaryRed.hover.border} !important;
    };
    &:focus {
      color:  ${getButtonTheme().secondaryRed.hover.text} !important;
      background-color: ${getButtonTheme().secondaryRed.hover.background} !important;
      border-color: ${getButtonTheme().secondaryRed.hover.border} !important;
    };
    &:active {
      color:  ${getButtonTheme().secondaryRed.active.text} !important;
      background-color: ${getButtonTheme().secondaryRed.active.background} !important;
      border-color: ${getButtonTheme().secondaryRed.active.border} !important;
    };
    &:disabled {
      color:  ${getButtonTheme().secondaryRed.disabled.text} !important;
      background-color: ${getButtonTheme().secondaryRed.disabled.background} !important;
      border-color: ${getButtonTheme().secondaryRed.disabled.border} !important;
    };
    `,
  loadMore: `
    overflow: hidden;
    background-color: ${getButtonTheme().loadMore.background} !important;
    color: ${getButtonTheme().loadMore.text} !important;
    border-color: ${getButtonTheme().loadMore.border} !important;
    &:active,
    &:focus,
    &:hover {
      background-color: ${getButtonTheme().loadMore.background} !important;
      color: ${getButtonTheme().loadMore.text} !important;
      border-color:  ${getButtonTheme().loadMore.border} !important;
      box-shadow: none;
    };
    `,
  gradientTeal: `
    background: ${getButtonTheme().gradientTeal.background} !important;
    color:  ${getButtonTheme().gradientTeal.text} !important;
    border: none;
    box-shadow: none;
    &:active {
      box-shadow: none;
      background: ${getButtonTheme().gradientTeal.active.background} !important;
      color: ${getButtonTheme().gradientTeal.active.text} !important;
    }
    &:focus,
    &:hover {
      box-shadow: none;
      background: ${getButtonTheme().gradientTeal.hover.background} !important;
      color:  ${getButtonTheme().gradientTeal.hover.text} !important;
    };
    `,
});

const buttonSize = {
  small: {
    borderRadius: '4px',
    height: '30px',
    padding: '0 12px',
    fontSize: `${type.SM.fontSize} !important`,
  },
  medium: {
    borderRadius: '6px',
    height: '42px',
    padding: '0 16px',
    fontSize: `${type.MD.fontSize} !important`,
  },
  large: {
    borderRadius: '6px',
    height: '50px',
    padding: '0 40px',
    fontSize: `${type.LG.fontSize} !important`,
  },
};

const StyledButton = styled('button')<IStyledButton>(
  {
    fontWeight: 600,
    borderStyle: 'none',
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    cursor: 'pointer',
    borderWidth: '1px',
    fontFamily: `${FONT_FAMILIES.primary} !important`,
    '&:focus': {
      outline: 'none',
    },
  },
  ({ buttontype }) => getButtonStyle()[buttontype],
  ({ size }) => buttonSize[size],
  ({ customStyle }) => ({ ...customStyle })
);

const fitStyledButton = {
  display: 'block',
  boxSizing: 'border-box',
  width: '100%',
};

const StyledButtonV2 = styled(ButtonV2)<{ buttontype: string; style: React.CSSProperties }>(
  {
    borderRadius: '6px !important',
    padding: '15px 22px',
    borderWidth: isMobile ? '1px' : '2px',
    fontFamily: `${FONT_FAMILIES.primary} !important`,
    [breakpoints.down(639.05)]: {
      borderRadius: '4px !important',
    },
  },
  ({ buttontype }) => getButtonStyle()[buttontype],
  ({ style }) => ({ ...style })
);

const Content = styled('div')<IContent>(
  {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
    fontFamily: `${FONT_FAMILIES.primary} !important`,
  },
  ({ alignContent }) =>
    alignContent === 'left' && {
      justifyContent: 'flex-start',
    }
);

const IconContainer = styled('div')<IIconContainer>(
  {
    display: 'inline-flex',
    justifyContent: 'center',
  },
  ({ iconPosition }) =>
    iconPosition === 'right'
      ? {
          order: 2,
          marginLeft: 4,
        }
      : {
          marginRight: 4,
        },
  ({ noMargin }) => noMargin && { marginRight: 0 }
);

const ButtonText = styled('div')`
  display: inline-flex;
  font-family: ${FONT_FAMILIES.primary};
`;

const Button = (props: IButtonProps): ReactElement => {
  const {
    to,
    buttonType = 'default',
    size = 'medium',
    fit,
    customStyle,
    icon,
    iconPosition,
    alignContent,
    children,
    isDefault,
    buttonRef,
    onClick,
    buttonTextCustomStyle,
    customBtnContentStyle = {},
    ...rest
  } = props;

  const button = (
    <StyledButton
      ref={buttonRef}
      buttontype={buttonType}
      size={size}
      style={fit ? (fitStyledButton as React.CSSProperties) : {}}
      customStyle={customStyle}
      onClick={onClick}
      {...rest}
    >
      <Content alignContent={alignContent}>
        {icon && (
          <IconContainer iconPosition={iconPosition} noMargin={React.Children.count(children) === 0}>
            {icon}
          </IconContainer>
        )}
        <ButtonText>{children}</ButtonText>
      </Content>
    </StyledButton>
  );

  const buttonSWui = (
    <StyledButtonV2
      ref={buttonRef}
      data-swui-id={`swui-${buttonType}`}
      buttontype={buttonType}
      shape="curved"
      style={fit ? ({ ...customStyle, ...fitStyledButton } as React.CSSProperties) : customStyle}
      onClick={onClick}
      {...rest}
    >
      <Content alignContent={alignContent} style={customBtnContentStyle}>
        {icon && (
          <IconContainer iconPosition={iconPosition} noMargin={React.Children.count(children) === 0}>
            {icon}
          </IconContainer>
        )}
        <ButtonText style={buttonTextCustomStyle}>{children}</ButtonText>
      </Content>
    </StyledButtonV2>
  );

  const renderBtn = () => {
    return isDefault ? button : buttonSWui;
  };

  return <React.Fragment>{to ? <Link to={to}>{button}</Link> : renderBtn()}</React.Fragment>;
};

Button.defaultProps = {
  buttonType: 'normal',
  size: 'medium',
  iconPosition: 'left',
  alignContent: 'center',
  customStyle: {},
};

export default Button;
